import { useQuoteStore } from '../store'
import { clsx } from 'clsx'
import { TrendingUp, Zap, Calendar, Users, RefreshCw, Info } from 'lucide-react'
import { EXCHANGE_RATES } from '../data'

// Helper to format currency
const formatCurrency = (value: number, currency: string): string => {
  const code = currency || 'AUD'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}


// Metric display with optional badge
function MetricCard({
  label,
  value,
  sublabel,
  badge,
  badgeColor,
  highlight = false,
}: {
  label: string
  value: string
  sublabel?: string
  badge?: string
  badgeColor?: 'great' | 'good' | 'average' | 'poor'
  highlight?: boolean
}) {
  return (
    <div
      className={clsx(
        'rounded-lg p-4',
        highlight ? 'bg-primary/10 border border-primary/30' : 'bg-[var(--bg-tertiary)] border border-[var(--border-color)]'
      )}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-[var(--text-muted)]">{label}</span>
        {badge && (
          <span
            className={clsx(
              'text-[9px] px-1.5 py-0.5 rounded font-medium',
              badgeColor === 'great' && 'bg-success/20 text-success',
              badgeColor === 'good' && 'bg-info/20 text-info',
              badgeColor === 'average' && 'bg-warning/20 text-warning',
              badgeColor === 'poor' && 'bg-danger/20 text-danger'
            )}
          >
            {badge}
          </span>
        )}
      </div>
      <div
        className={clsx(
          'font-semibold text-lg',
          badgeColor === 'great' && 'text-success',
          badgeColor === 'good' && 'text-info',
          badgeColor === 'average' && 'text-warning',
          badgeColor === 'poor' && 'text-danger',
          highlight && !badgeColor && 'text-primary',
          !badgeColor && !highlight && 'text-[var(--text-primary)]'
        )}
      >
        {value}
      </div>
      {sublabel && <div className="text-[9px] text-[var(--text-muted)] mt-0.5">{sublabel}</div>}
    </div>
  )
}

// Map rating class to badge color
function getBadgeColor(ratingClass: string): 'great' | 'good' | 'average' | 'poor' {
  if (ratingClass.includes('great')) return 'great'
  if (ratingClass.includes('good')) return 'good'
  if (ratingClass.includes('average')) return 'average'
  return 'poor'
}

// Section component
function MetricSection({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: typeof TrendingUp
  children: React.ReactNode
}) {
  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
      <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4 flex items-center gap-2">
        <Icon size={16} className="text-primary" />
        {title}
      </h3>
      {children}
    </div>
  )
}

export function MetricsPanel() {
  const { calculations, details } = useQuoteStore()
  const { displayCurrency, hourlyRate } = details

  if (!calculations) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <TrendingUp size={16} className="text-primary" />
          Financial Metrics
        </h3>
        <p className="text-[11px] text-[var(--text-muted)] text-center py-8">
          Fill in deal details and add products to see metrics.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Currency Conversion Notice */}
      {displayCurrency !== 'AUD' && (
        <MetricSection title="Currency Conversion" icon={RefreshCw}>
          <div className="flex items-start gap-2 p-3 bg-[var(--bg-tertiary)] rounded-lg">
            <Info size={14} className="text-info flex-shrink-0 mt-0.5" />
            <div className="text-[11px]">
              <p className="text-[var(--text-primary)]">
                Displaying in <strong>{displayCurrency}</strong>. Exchange rate: 1 AUD ={' '}
                {EXCHANGE_RATES.AUD[displayCurrency]} {displayCurrency}
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="text-[var(--text-muted)]">
                  TCV in AUD:{' '}
                  <span className="text-[var(--text-primary)]">
                    {formatCurrency(calculations.tcv / (EXCHANGE_RATES.AUD[displayCurrency] || 1), 'AUD')}
                  </span>
                </div>
                <div className="text-primary">
                  TCV in {displayCurrency}: <span className="font-medium">{formatCurrency(calculations.tcv, displayCurrency)}</span>
                </div>
              </div>
            </div>
          </div>
        </MetricSection>
      )}

      {/* Revenue Metrics */}
      <MetricSection title="Revenue Metrics" icon={TrendingUp}>
        <div className="grid grid-cols-3 gap-3">
          <MetricCard
            label="MRR"
            value={formatCurrency(calculations.mrr, displayCurrency)}
            sublabel="Monthly Recurring Revenue"
          />
          <MetricCard
            label="ARR"
            value={formatCurrency(calculations.arr, displayCurrency)}
            sublabel="Annual Recurring Revenue"
          />
          <MetricCard
            label="TCV"
            value={formatCurrency(calculations.tcv, displayCurrency)}
            sublabel={`Total Contract Value (${details.contractTermMonths} mo)`}
            badge={calculations.tcvRating.rating}
            badgeColor={getBadgeColor(calculations.tcvRating.class)}
            highlight
          />
        </div>
      </MetricSection>

      {/* Profitability Metrics */}
      <MetricSection title="Profitability" icon={Zap}>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <MetricCard
            label="Gross Profit (Monthly)"
            value={formatCurrency(calculations.grossProfit / details.contractTermMonths, displayCurrency)}
          />
          <MetricCard
            label="Gross Profit (Contract)"
            value={formatCurrency(calculations.grossProfit, displayCurrency)}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <MetricCard
            label="Gross Margin"
            value={`${calculations.grossProfitMargin.toFixed(1)}%`}
            badge={calculations.marginRating.rating}
            badgeColor={getBadgeColor(calculations.marginRating.class)}
          />
          <MetricCard
            label="LTV/CAC"
            value={`${calculations.ltvCacRatio.toFixed(1)}x`}
            badge={calculations.ltvCacRating.rating}
            badgeColor={getBadgeColor(calculations.ltvCacRating.class)}
          />
          <MetricCard
            label="Payback"
            value={`${calculations.paybackMonths.toFixed(1)} mo`}
            badge={calculations.paybackRating.rating}
            badgeColor={getBadgeColor(calculations.paybackRating.class)}
          />
        </div>
      </MetricSection>

      {/* Unit Economics */}
      <MetricSection title="Unit Economics" icon={Users}>
        <div className="grid grid-cols-3 gap-3">
          <MetricCard
            label="LTV"
            value={formatCurrency(calculations.ltv, displayCurrency)}
            sublabel="Lifetime Value (3yr)"
          />
          <MetricCard
            label="CAC"
            value={formatCurrency(Math.abs(calculations.cac), displayCurrency)}
            sublabel="Est. Acquisition Cost"
          />
          <MetricCard
            label="PC3"
            value={formatCurrency(calculations.pc3, displayCurrency)}
            sublabel="Profit Contribution 3yr"
          />
        </div>
      </MetricSection>

      {/* Implementation Details */}
      <MetricSection title="Implementation" icon={Calendar}>
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            label="Hourly Rate"
            value={formatCurrency(hourlyRate, displayCurrency)}
          />
          <MetricCard
            label="Implementation Fee"
            value={formatCurrency(details.implementationFee, displayCurrency)}
            highlight
          />
        </div>
      </MetricSection>

      {/* Partner Impact (if applicable) */}
      {details.partnerCommission > 0 && (
        <MetricSection title="Partner Impact" icon={Users}>
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              label="Partner Commission"
              value={formatCurrency((calculations.tcv * details.partnerCommission) / 100, displayCurrency)}
              sublabel={`${details.partnerCommission}% of TCV`}
            />
            <MetricCard
              label="Net TCV"
              value={formatCurrency(calculations.tcv * (1 - details.partnerCommission / 100), displayCurrency)}
              sublabel="After partner commission"
            />
          </div>
        </MetricSection>
      )}
    </div>
  )
}
